// Operational Safety Layer — Fase 14.5
import { obs } from '@/core/observability'

export type SafeModule = 'twin' | 'vision' | 'tv' | 'ai' | 'analytics' | 'realtime' | 'observability' | 'autonomous'
export type CircuitState = 'closed' | 'open' | 'half-open'

interface CircuitBreaker { module: SafeModule; state: CircuitState; failures: number; lastFailureAt: number; openedAt?: number; halfOpenAt?: number }

const HALF_OPEN_DELAY_MS = 30_000
const DEFAULT_THRESHOLD  = 5
const _circuits   = new Map<SafeModule, CircuitBreaker>()
const _killSwitches = new Set<SafeModule>()
let   _safeMode   = false

export function killSwitch(module: SafeModule): void { _killSwitches.add(module); obs.warn('safety', `Kill switch: ${module}`) }
export function clearKillSwitch(module: SafeModule): void { _killSwitches.delete(module); obs.info('safety', `Kill switch cleared: ${module}`) }
export function isKilled(module: SafeModule): boolean { return _killSwitches.has(module) }
export function enterSafeMode(): void { _safeMode = true; obs.warn('safety', 'SAFE MODE activated') }
export function exitSafeMode(): void { _safeMode = false; obs.info('safety', 'Safe mode exited') }
export function isSafeMode(): boolean { return _safeMode }

const SAFE_MODE_ALLOWED: SafeModule[] = ['tv', 'observability']

function getCircuit(module: SafeModule): CircuitBreaker {
  if (!_circuits.has(module)) _circuits.set(module, { module, state: 'closed', failures: 0, lastFailureAt: 0 })
  return _circuits.get(module)!
}

export function recordFailure(module: SafeModule, threshold = DEFAULT_THRESHOLD): void {
  const c = getCircuit(module); c.failures++; c.lastFailureAt = Date.now()
  if (c.state === 'closed' && c.failures >= threshold) { c.state = 'open'; c.openedAt = Date.now(); obs.warn('safety', `Circuit OPEN: ${module}`) }
  else if (c.state === 'half-open') { c.state = 'open'; c.openedAt = Date.now(); obs.warn('safety', `Circuit re-OPEN: ${module}`) }
}

export function recordSuccess(module: SafeModule): void {
  const c = getCircuit(module)
  if (c.state === 'half-open') { c.state = 'closed'; c.failures = 0; obs.info('safety', `Circuit CLOSED: ${module}`) }
  else if (c.state === 'closed') c.failures = Math.max(0, c.failures - 1)
}

export function canRun(module: SafeModule): boolean {
  if (_killSwitches.has(module)) return false
  if (_safeMode && !SAFE_MODE_ALLOWED.includes(module)) return false
  const c = getCircuit(module)
  if (c.state === 'open') {
    if (c.openedAt && Date.now() - c.openedAt >= HALF_OPEN_DELAY_MS) { c.state = 'half-open'; c.halfOpenAt = Date.now(); obs.info('safety', `Circuit HALF-OPEN: ${module}`); return true }
    return false
  }
  return true
}

export async function withCircuitBreaker<T>(module: SafeModule, op: () => Promise<T>, fallback?: () => T): Promise<T | null> {
  if (!canRun(module)) return fallback ? fallback() : null
  try { const r = await op(); recordSuccess(module); return r }
  catch (err) { recordFailure(module); obs.warn('safety', `Module ${module} threw: ${String(err)}`); return fallback ? fallback() : null }
}

export type DegradedReason = 'realtime_down' | 'db_unreachable' | 'memory_critical' | 'engine_failure'
interface DegradedState { active: boolean; reason?: DegradedReason; since?: number; module?: SafeModule }

let _degraded: DegradedState = { active: false }
const _degradedListeners: Array<(s: DegradedState) => void> = []

export function enterDegradedMode(reason: DegradedReason, module?: SafeModule): void {
  if (_degraded.active) return
  _degraded = { active: true, reason, since: Date.now(), module }
  obs.warn('safety', `DEGRADED: ${reason}`, { module })
  _degradedListeners.forEach((fn) => { try { fn(_degraded) } catch { /* ignore */ } })
}
export function exitDegradedMode(): void { if (!_degraded.active) return; _degraded = { active: false }; obs.info('safety', 'Degraded mode exited'); _degradedListeners.forEach((fn) => { try { fn(_degraded) } catch { /* ignore */ } }) }
export function isDegraded(): boolean { return _degraded.active }
export function getDegradedState(): Readonly<DegradedState> { return _degraded }
export function onDegradedChange(fn: (s: DegradedState) => void): () => void { _degradedListeners.push(fn); return () => { const i = _degradedListeners.indexOf(fn); if (i >= 0) _degradedListeners.splice(i, 1) } }

export interface SafetySnapshot { safeMode: boolean; degraded: DegradedState; killSwitches: SafeModule[]; circuits: Array<{ module: SafeModule; state: CircuitState; failures: number }> }
export function getSafetySnapshot(): SafetySnapshot { return { safeMode: _safeMode, degraded: { ..._degraded }, killSwitches: [..._killSwitches], circuits: [..._circuits.values()].map((c) => ({ module: c.module, state: c.state, failures: c.failures })) } }
export function resetSafetyLayer(): void { _safeMode = false; _degraded = { active: false }; _killSwitches.clear(); _circuits.clear() }
